/**
 * Messaging deep-link helpers.
 *
 * Both WhatsApp and SMS use URI schemes that open the user's app with
 * a pre-filled message. Zero API, zero cost, admin's device sends.
 *
 * Phone numbers are E.164 digits-only (no leading +) to match the
 * existing formatPhone() helper used by the WhatsApp call sites.
 */

/** Build a `sms:+E164?body=...` deep link.
 *  Works on iOS, Android, and macOS Messages. */
export function getSmsDeepLink(phoneE164NoPlus: string, message: string): string {
  return `sms:+${phoneE164NoPlus}?body=${encodeURIComponent(message)}`;
}

/** Build a `https://wa.me/E164?text=...` deep link.
 *  Phone is digits only with no leading +. */
export function getWhatsAppDeepLink(phoneE164NoPlus: string, message: string): string {
  return `https://wa.me/${phoneE164NoPlus}?text=${encodeURIComponent(message)}`;
}

/**
 * Normalise a stored phone number to E.164 digits (no leading '+') ready
 * for wa.me / sms: deep links.
 *
 * Strips every non-digit, then if the result starts with a local '0',
 * swaps it for the country dialling prefix taken from
 * country_config.phone_prefix (e.g. '+27' → '27', '+45' → '45').
 *
 * Returns null if the input is empty, missing, or the result is shorter
 * than 9 digits (almost certainly garbage; wa.me would open the contact
 * picker instead of the chat).
 */
export function formatPhoneE164(
  phone: string | null | undefined,
  phonePrefix: string | null | undefined,
): string | null {
  if (!phone) return null;
  let cleaned = phone.replace(/\D/g, '');
  if (cleaned.startsWith('0')) {
    const prefix = (phonePrefix || '').replace(/^\+/, '');
    if (prefix) {
      cleaned = prefix + cleaned.substring(1);
    }
  }
  if (cleaned.length < 9) return null;
  return cleaned;
}
